import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, Link, Stack, ToggleButtonGroup, ToggleButton, Stepper, Step, StepLabel, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { usePasswordReset } from '../../hooks/auth/usePasswordReset';
import FormInput from '../inputs/FormInput';
import Btn from '../global/Btn';
import OtpInput from './OtpInput';
import OtpCountdownTimer from './OtpCountdownTimer';
import OtpResendButton from './OtpResendButton';
import { ArrowForward, ArrowBack, LockReset } from '@mui/icons-material';
import { maskEmail, maskPhoneNumber } from '../../utils/otpHelpers';
import { Alert } from '@mui/material';

const FormForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const passwordReset = usePasswordReset();

    const [step, setStep] = useState(1); // 1: Request, 2: Reset
    const [channel, setChannel] = useState('sms');
    const [email, setEmail] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState('965');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const identifier = channel === 'sms' ? `+${selectedCountryCode}${phoneNumber}` : email;

    const handleRequestOtp = () => {
        if (channel === 'sms' && !phoneNumber) {
            toast.error(t('validation.phoneRequired'));
            return;
        }
        if (channel === 'email' && !email) {
            toast.error(t('validation.emailRequired'));
            return;
        }

        passwordReset.request.mutate(
            { channel, identifier },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    setStep(2);
                    setOtp('');
                },
                onError: (error) => {
                    toast.error(error?.response?.data?.message || t('otp.send_failed'));
                }
            }
        );
    };

    const handleResetPassword = () => {
        if (!otp || otp.length < 6) {
            toast.error(t('otp.invalid_code'));
            return;
        }
        if (!password || password.length < 8) {
            toast.error(t('validation.passwordTooShort'));
            return;
        }
        if (password !== confirmPassword) {
            toast.error(t('validation.passwordsNotMatch'));
            return;
        }

        passwordReset.reset.mutate(
            { channel, identifier, otp, password, password_confirmation: confirmPassword },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('auth.passwordResetSuccess'));
                    navigate('/login');
                },
                onError: (error) => {
                    toast.error(error?.response?.data?.message || t('common.error'));
                }
            }
        );
    };

    const handleResend = () => {
        passwordReset.resend.mutate(
            { channel, identifier },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_resent'));
                    setOtp('');
                },
                onError: (error) => {
                    toast.error(error?.response?.data?.message || t('otp.resend_failed'));
                }
            }
        );
    };

    const handleExpire = useCallback(() => {
        // Only show expired toast if we are not currently resending
        if (!passwordReset.resend.isPending) {
            toast.error(t('otp.code_expired'));
        }
    }, [passwordReset.resend.isPending, t]);

    const steps = [t('otp.details'), t('otp.verification')];

    return (
        <Paper
            elevation={4}
            sx={{
                px: { md: 5, xs: 3 },
                py: { md: 5, xs: 4 },
                borderRadius: 4,
                maxWidth: 520,
                mx: 'auto',
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 10px 40px rgba(0,0,0,0.35)'
                    : '0 10px 40px rgba(0,0,0,0.08)',
            }}
        >
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Avatar
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        width: 56,
                        height: 56,
                    }}
                >
                    <LockReset fontSize="medium" />
                </Avatar>
                <Typography variant="h5" textAlign="center" sx={{ fontWeight: 700 }}>
                    {step === 1 ? t('auth.forgotPasswordTitle') : t('auth.resetPasswordTitle')}
                </Typography>
            </Stack>

            <Stepper activeStep={step - 1} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {step === 1 ? (
                <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('auth.forgotPasswordSubtitle')}
                    </Typography>

                    <ToggleButtonGroup
                        value={channel}
                        exclusive
                        onChange={(e, v) => v && setChannel(v)}
                        fullWidth
                        size="small"
                    >
                        <ToggleButton value="sms">{t('otp.phone')}</ToggleButton>
                        <ToggleButton value="email">{t('otp.email')}</ToggleButton>
                    </ToggleButtonGroup>

                    {channel === 'sms' ? (
                        <FormInput
                            type="tel"
                            label={t('auth.phone')}
                            placeholder="555-555-555"
                            selectedCountryCode={selectedCountryCode}
                            setSelectedCountryCode={setSelectedCountryCode}
                            phoneNumber={phoneNumber}
                            setPhoneNumber={setPhoneNumber}
                        />
                    ) : (
                        <FormInput
                            label={t('auth.email')}
                            type="email"
                            placeholder="example@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    )}

                    <Btn
                        onClick={handleRequestOtp}
                        loading={passwordReset.request.isPending}
                        endIcon={<ArrowForward />}
                        width="100%"
                        widthMob="100%"
                        sx={{ mx: 'auto' }}
                    >
                        {t('auth.sendResetCode')}
                    </Btn>

                    <Box textAlign="center">
                        <Link component={RouterLink} to="/login" sx={{ textDecoration: 'none', fontWeight: 600 }}>
                            {t('auth.backToLogin')}
                        </Link>
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('auth.resetPasswordSubtitle')}
                    </Typography>

                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {t('otp.sent_to')}:{' '}
                        <strong>
                            {channel === 'sms'
                                ? maskPhoneNumber(identifier)
                                : maskEmail(identifier)
                            }
                        </strong>
                    </Alert>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" mb={1}>
                            {t('otp.verification')}
                        </Typography>
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OtpCountdownTimer
                            key={passwordReset.request.data?.expires_at}
                            expiresAt={passwordReset.request.data?.expires_at}
                            onExpire={handleExpire}
                        />
                        <OtpResendButton
                            onResend={handleResend}
                            loading={passwordReset.resend.isPending}
                            cooldownSeconds={passwordReset.resend.data?.cooldown_seconds || 60}
                        />
                    </Box>

                    <FormInput
                        label={t('auth.password')}
                        type="password"
                        placeholder="********"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <FormInput
                        label={t('auth.confirmPassword')}
                        type="password"
                        placeholder="********"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />

                    <Stack spacing={2}>
                        <Btn
                            onClick={handleResetPassword}
                            loading={passwordReset.reset.isPending}
                            width="100%"
                            widthMob="100%"
                        >
                            {t('auth.resetPassword')}
                        </Btn>
                        <Btn
                            variant="outlined"
                            bg="secondary"
                            onClick={() => setStep(1)}
                            startIcon={<ArrowBack />}
                            width="100%"
                            widthMob="100%"
                        >
                            {t('common.back')}
                        </Btn>
                    </Stack>
                </Stack>
            )}
        </Paper>
    );
};

export default FormForgotPassword;
